/**
 * Error Boundary Component for Reporting System
 * 
 * Catches JavaScript errors and provides fallback UI for reporting components
 */

import React from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { BsExclamationTriangle, BsArrowClockwise } from 'react-icons/bs';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null 
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Log error to console for debugging
    }

    handleRetry = () => {
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null 
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <Card className="border-danger">
                    <Card.Body className="text-center py-5">
                        <BsExclamationTriangle className="text-danger display-4 mb-3" />
                        <h5 className="text-danger mb-3">Something went wrong</h5>
                        <p className="text-muted mb-4">
                            {this.props.fallbackMessage || 
                            'An error occurred while loading the reporting component. Please try again or contact support if the problem persists.'}
                        </p>
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Alert variant="warning" className="text-start small mb-4">
                                <strong>Error Details:</strong><br />
                                {this.state.error.toString()}
                                {this.state.errorInfo?.componentStack && (
                                    <>
                                        <br /><br />
                                        <strong>Component Stack:</strong>
                                        <pre className="mt-2" style={{ fontSize: '0.75rem' }}>
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </>
                                )}
                            </Alert>
                        )}
                        
                        <div className="d-flex justify-content-center gap-2">
                            <Button 
                                variant="primary" 
                                onClick={this.handleRetry}
                                className="d-flex align-items-center"
                            >
                                <BsArrowClockwise className="me-2" />
                                Try Again
                            </Button>
                            
                            {this.props.onError && (
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => this.props.onError(this.state.error)}
                                >
                                    Report Issue
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;